'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * §7 — the synthetic scan feed. Prepends a row every 2300 ms while allowed
 * (motion flag, tab visible, no reduced-motion, md+, panel ≥25% in view),
 * capped at 6 rows, fresh dot only on the newest. The SSR/reduced-motion/
 * mobile state is the deterministic 5-row sample below (fixed times — no
 * clock in render, hydration-safe); when the loop is allowed, the sample is
 * re-stamped with real wall-clock times on mount and rows arrive stamped
 * live. Labeled SYNTHETIC SAMPLE on-page (honesty rail — keep until it's a
 * real feed). Columns are a subset of the dashboard's live request log
 * (frontend-web-user analytics/logs: Timestamp, Country, Device, Status,
 * Path) — country-level geo only, no destination column.
 *
 * Real-clock interval by design (handoff motion contract): rAF starves in
 * occluded tabs, and a skipped beat must simply pause the feed, not queue.
 */

const MOTION = true;

const POOL = [
  { country: 'DE', dev: 'Mobile', status: '302', path: '/karte' },
  { country: 'NL', dev: 'Mobile', status: '302', path: '/menu' },
  { country: 'AT', dev: 'Tablet', status: '302', path: '/karte' },
  { country: 'FR', dev: 'Mobile', status: '302', path: '/menu' },
  { country: 'ES', dev: 'Mobile', status: '302', path: '/menu' },
  { country: 'DE', dev: 'Mobile', status: '302', path: '/karte' },
  { country: 'IT', dev: 'Tablet', status: '302', path: '/menu' },
  { country: 'AT', dev: 'Mobile', status: '302', path: '/karte' },
] as const;

type Row = {
  id: number;
  country: string;
  dev: string;
  status: string;
  path: string;
  t: string;
  fresh: boolean;
};

/** Sample offsets (seconds before "now") shared by SSR sample + live re-stamp. */
const SAMPLE_OFFSETS = [-4, -11, -19, -28, -41];
/** Deterministic sample times: 09:41:00 + offsets — the resting state. */
const SAMPLE_TIMES = ['09:40:56', '09:40:49', '09:40:41', '09:40:32', '09:40:19'];

const INITIAL: Row[] = SAMPLE_TIMES.map((t, i) => ({
  id: -1 - i,
  ...POOL[i],
  t,
  fresh: i === 0,
}));

const fmt = (ms: number) => {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

const ROW_GRID =
  'grid grid-cols-[56px_60px_76px_1fr] gap-2 md:grid-cols-[70px_92px_74px_118px_1fr] md:gap-3';

export default function ScanFeed() {
  const [feed, setFeed] = useState<Row[]>(INITIAL);
  const feedI = useRef(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const visibleRef = useRef(false);

  useEffect(() => {
    if (!MOTION) return;
    const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mqWide = window.matchMedia('(min-width: 768px)');

    // Live re-stamp of the sample times, only when the loop can ever run.
    if (mqWide.matches && !mqReduce.matches) {
      const now = Date.now();
      setFeed((prev) => prev.map((r, i) => ({ ...r, t: fmt(now + SAMPLE_OFFSETS[i] * 1000) })));
    }

    const io = new IntersectionObserver(
      (entries) => {
        visibleRef.current = entries[entries.length - 1].isIntersecting;
      },
      { threshold: 0.25 },
    );
    if (panelRef.current) io.observe(panelRef.current);

    const allowed = () =>
      !document.hidden && !mqReduce.matches && mqWide.matches && visibleRef.current;

    const id = setInterval(() => {
      if (!allowed()) return;
      const i = feedI.current++;
      const row: Row = { id: i, ...POOL[(i + 5) % POOL.length], t: fmt(Date.now()), fresh: true };
      setFeed((prev) => [row, ...prev.map((r) => ({ ...r, fresh: false }))].slice(0, 6));
    }, 2300);

    return () => {
      clearInterval(id);
      io.disconnect();
    };
  }, []);

  return (
    <div
      ref={panelRef}
      className="min-w-[300px] flex-[1_1_460px] self-start rounded-ref border border-white/[0.14] bg-white/[0.03] px-[22px] py-5"
    >
      <p className="sr-only">
        Synthetic sample of scans in the request log: time, country, device, status and
        path for each scan &mdash; the same columns the dashboard&rsquo;s live request log shows.
      </p>
      <div aria-hidden="true">
        <div
          className={`${ROW_GRID} border-b border-white/[0.12] pb-3 font-jetbrains text-[9px] tracking-[0.12em] text-white/40`}
        >
          <span>TIME</span>
          <span>COUNTRY</span>
          <span className="max-md:hidden">DEVICE</span>
          <span>STATUS</span>
          <span>PATH</span>
        </div>
        {/* min-h reserves the capped 6-row height so the one-time 5→6 growth
            never moves the layout below (measured: 6 × 38.5px + border, QA). */}
        <div className="md:min-h-[237px]">
          {feed.map((row) => (
            <div
              key={row.id}
              className={`${ROW_GRID} items-center border-b border-white/[0.07] py-[11px] font-jetbrains text-[10px] md:text-[11px]`}
            >
              <span className="flex items-center gap-[7px] text-white/45">
                {row.fresh && (
                  <span className="h-[5px] w-[5px] flex-none rounded-full bg-data-green" />
                )}
                {row.t}
              </span>
              <span className="text-white">{row.country}</span>
              <span className="text-white/60 max-md:hidden">{row.dev}</span>
              <span className="text-data-cyan">{row.status}</span>
              <span className="truncate text-white/75">{row.path}</span>
            </div>
          ))}
        </div>
      </div>
      <span className="block pt-3 font-jetbrains text-[9px] tracking-[0.12em] text-white/35">
        SYNTHETIC SAMPLE &mdash; COLUMNS FROM THE LIVE REQUEST LOG
      </span>
    </div>
  );
}
